// Mock data for demo application

export const MOCK_USER_PROFILE = {
  id: 'demo-user-1',
  firstName: 'John',
  lastName: 'Doe',
  email: 'john.doe@example.com',
  emailVerified: true
};

export const MOCK_VIVASCORE = {
  score: 72,
  previousScore: 68,
  lastUpdated: new Date(),
  dateCalculated: new Date().toISOString(),
  trend: 'up'
};

export const MOCK_PILLARS = [
  {
    id: 'heart-bp',
    name: 'Heart & Blood Pressure',
    score: 75,
    status: 'good',
    confidencePercentage: 60,
    missingDataPoints: [
      {
        dataPointKey: 'hrv_sdnn',
        dataPointName: 'HRV SDNN',
        actionType: 'complete_scan',
        actionDescription: 'Complete a scan to measure HRV',
        estimatedConfidenceIncrease: 20
      },
      {
        dataPointKey: 'resting_heart_rate',
        dataPointName: 'Resting Heart Rate',
        actionType: 'sync_wearable',
        actionDescription: 'Sync your wearable device',
        estimatedConfidenceIncrease: 20
      }
    ]
  },
  {
    id: 'sleep-recovery',
    name: 'Sleep & Recovery',
    score: 68,
    status: 'average',
    confidencePercentage: 40,
    missingDataPoints: [
      {
        dataPointKey: 'sleep_stages',
        dataPointName: 'Sleep Stages',
        actionType: 'sync_wearable',
        actionDescription: 'Connect your fitness tracker to track sleep stages',
        estimatedConfidenceIncrease: 30
      },
      {
        dataPointKey: 'waso',
        dataPointName: 'Wake After Sleep Onset',
        actionType: 'sync_wearable',
        actionDescription: 'Sync your wearable device',
        estimatedConfidenceIncrease: 15
      },
      {
        dataPointKey: 'mood_log',
        dataPointName: 'Mood Log',
        actionType: 'log_emotion',
        actionDescription: 'Log your mood today',
        estimatedConfidenceIncrease: 15
      }
    ]
  },
  {
    id: 'activity-movement',
    name: 'Activity & Movement',
    score: 80,
    status: 'good',
    confidencePercentage: 100,
    missingDataPoints: []
  },
  {
    id: 'metabolic-weight',
    name: 'Metabolic & Weight',
    score: 65,
    status: 'average',
    confidencePercentage: 50,
    missingDataPoints: [
      {
        dataPointKey: 'body_fat_percentage',
        dataPointName: 'Body Fat %',
        actionType: 'enter_manual_data',
        actionDescription: 'Enter your body composition data',
        estimatedConfidenceIncrease: 25
      },
      {
        dataPointKey: 'waist_circumference',
        dataPointName: 'Waist Circumference',
        actionType: 'enter_manual_data',
        actionDescription: 'Enter your waist measurement',
        estimatedConfidenceIncrease: 25
      }
    ]
  },
  {
    id: 'mental-wellbeing',
    name: 'Mental Wellbeing',
    score: 70,
    status: 'good',
    confidencePercentage: 30,
    missingDataPoints: [
      {
        dataPointKey: 'emotion_log',
        dataPointName: 'Emotion Log',
        actionType: 'log_emotion',
        actionDescription: 'Log your mood today—it only takes 10 seconds!',
        estimatedConfidenceIncrease: 35
      },
      {
        dataPointKey: 'mental_wellness_questionnaire',
        dataPointName: 'Mental Wellness Questionnaire',
        actionType: 'complete_questionnaire',
        actionDescription: 'Complete a mental wellness assessment',
        estimatedConfidenceIncrease: 35
      }
    ]
  }
];

export const MOCK_POINTS_BALANCE = {
  currentBalance: 1250,
  lifetimeEarned: 3500,
  lifetimeSpent: 2250
};

export const MOCK_BADGES = [
  {
    id: 'streak-7',
    name: '7-Day Streak',
    description: 'Complete scans for 7 consecutive days',
    tier: 'Bronze',
    earned: true,
    earnedDate: new Date('2024-01-15')
  },
  {
    id: 'heart-guardian',
    name: 'Heart Health Guardian',
    description: 'Maintain excellent heart health for 30 days',
    tier: 'Silver',
    earned: true,
    earnedDate: new Date('2024-01-20')
  }
];

export const MOCK_NOTIFICATIONS = [
  {
    id: 'notif-1',
    title: 'New Badge Earned!',
    notificationText: 'Congratulations! You\'ve earned the Heart Health Guardian badge.',
    eventDate: new Date(),
    messageWasRead: false,
    iconUrl: '/assets/v3/Notifications.svg',
    type: 'badge-earned',
    badgeId: 'heart-guardian'
  },
  {
    id: 'notif-2',
    title: 'Game Unlocked!',
    notificationText: 'Pacman is now unlocked for 24 hours! Tap to play.',
    eventDate: new Date(),
    messageWasRead: false,
    iconUrl: '/assets/v3/Notifications.svg',
    type: 'game-unlocked',
    gameId: 'Pacman',
    badgeId: 'heart-guardian'
  },
  {
    id: 'notif-3',
    title: 'Daily Mission Available',
    notificationText: 'Complete today\'s quiz to earn 50 points!',
    eventDate: new Date(),
    messageWasRead: false,
    iconUrl: '/assets/v3/Notifications.svg'
  }
];

export const MOCK_DASHBOARD_DATA = {
  healthScore: 72,
  trends: {
    healthScore: [65, 68, 70, 72, 72],
    dates: ['2024-01-01', '2024-01-08', '2024-01-15', '2024-01-22', '2024-01-29']
  }
};

// Mock observations (biometric observations)
export const MOCK_OBSERVATIONS = {
  observations: [
    {
      name: 'Blood Pressure Risk Score',
      displayValue: '75',
      unit: '',
      rangeLevel: 'Good',
      value: 75,
      previousValue: 72,
      observationType: 'BloodPressureRisk'
    },
    {
      name: 'Heart Rate',
      displayValue: '68',
      unit: 'bpm',
      rangeLevel: 'Excellent',
      value: 68,
      previousValue: 70,
      observationType: 'HeartRate'
    },
    {
      name: 'Sleep Score',
      displayValue: '82',
      unit: '',
      rangeLevel: 'Excellent',
      value: 82,
      previousValue: 78,
      observationType: 'SleepScore'
    },
    {
      name: 'Stress Level',
      displayValue: '45',
      unit: '',
      rangeLevel: 'Good',
      value: 45,
      previousValue: 50,
      observationType: 'StressLevel'
    },
    {
      name: 'Oxygen Saturation',
      displayValue: '98',
      unit: '%',
      rangeLevel: 'Excellent',
      value: 98,
      previousValue: 97,
      observationType: 'OxygenSaturation'
    },
    {
      name: 'BMI',
      displayValue: '24.5',
      unit: '',
      rangeLevel: 'Good',
      value: 24.5,
      previousValue: 25.0,
      observationType: 'BMI'
    }
  ]
};

// Mock goals
export const MOCK_GOALS = [
  {
    name: 'Daily Steps',
    value: 8500,
    goalValue: 10000,
    goalBaseValue: 10000,
    goalMaxValue: 20000,
    goalStepValue: 500,
    metricType: 'Steps'
  },
  {
    name: 'Sleep Duration',
    value: 420, // minutes
    goalValue: 480,
    goalBaseValue: 480,
    goalMaxValue: 600,
    goalStepValue: 30,
    metricType: 'Sleep'
  },
  {
    name: 'Active Time',
    value: 180, // minutes
    goalValue: 240,
    goalBaseValue: 240,
    goalMaxValue: 360,
    goalStepValue: 15,
    metricType: 'ActiveTime'
  }
];

// Mock workout data
export const MOCK_WORKOUTS = [
  {
    "startTime": "2025-12-01T17:12:44.632+00:00",
    "endTime": "2025-12-01T18:03:21.98+00:00",
    "durationSeconds": 3037,
    "totalEnergyBurnedKCal": 130,
    "totalDistanceMeters": 1700,
    "workoutType": "Walking",
    "restSeconds": null,
    "activeSeconds": 3037,
    "averageHeartRate": 98,
    "minHeartRate": 80,
    "maxHeartRate": 116,
    "appleHealthKitWorkoutType": 52,
    "googleHealthConnectWorkoutType": null,
    "calculatedRange": "Fair",
    "score": 2.3333333333333335,
    "sourceName": "Willie's Apple Watch"
  },
  {
    "startTime": "2025-11-30T11:26:34.624+00:00",
    "endTime": "2025-11-30T12:40:15.222+00:00",
    "durationSeconds": 4421,
    "totalEnergyBurnedKCal": 421,
    "totalDistanceMeters": null,
    "workoutType": "Traditional Strength Training",
    "restSeconds": null,
    "activeSeconds": 4421,
    "averageHeartRate": 96,
    "minHeartRate": 69,
    "maxHeartRate": 136,
    "appleHealthKitWorkoutType": 50,
    "googleHealthConnectWorkoutType": null,
    "calculatedRange": "Average",
    "score": 3.3333333333333335,
    "sourceName": "Zing"
  },
  {
    "startTime": "2025-11-30T08:54:23.491+00:00",
    "endTime": "2025-11-30T09:53:24.084+00:00",
    "durationSeconds": 3541,
    "totalEnergyBurnedKCal": 223,
    "totalDistanceMeters": 3133,
    "workoutType": "Walking",
    "restSeconds": null,
    "activeSeconds": 3541,
    "averageHeartRate": 104,
    "minHeartRate": 83,
    "maxHeartRate": 128,
    "appleHealthKitWorkoutType": 52,
    "googleHealthConnectWorkoutType": null,
    "calculatedRange": "Average",
    "score": 3,
    "sourceName": "Willie's Apple Watch"
  },
  {
    "startTime": "2025-11-29T17:50:32.983+00:00",
    "endTime": "2025-11-29T18:28:09.887+00:00",
    "durationSeconds": 2257,
    "totalEnergyBurnedKCal": 118,
    "totalDistanceMeters": 1375,
    "workoutType": "Walking",
    "restSeconds": null,
    "activeSeconds": 2257,
    "averageHeartRate": 101,
    "minHeartRate": 85,
    "maxHeartRate": 113,
    "appleHealthKitWorkoutType": 52,
    "googleHealthConnectWorkoutType": null,
    "calculatedRange": "Fair",
    "score": 2.3333333333333335,
    "sourceName": "Willie's Apple Watch"
  },
  {
    "startTime": "2025-11-29T10:49:19.801+00:00",
    "endTime": "2025-11-29T11:28:03.232+00:00",
    "durationSeconds": 2323,
    "totalEnergyBurnedKCal": 127,
    "totalDistanceMeters": 1839,
    "workoutType": "Walking",
    "restSeconds": null,
    "activeSeconds": 2323,
    "averageHeartRate": 109,
    "minHeartRate": 96,
    "maxHeartRate": 117,
    "appleHealthKitWorkoutType": 52,
    "googleHealthConnectWorkoutType": null,
    "calculatedRange": "Fair",
    "score": 2.3333333333333335,
    "sourceName": "Willie's Apple Watch"
  },
  {
    "startTime": "2025-11-27T11:23:23.342+00:00",
    "endTime": "2025-11-27T11:52:04.922+00:00",
    "durationSeconds": 1722,
    "totalEnergyBurnedKCal": 95,
    "totalDistanceMeters": 1557,
    "workoutType": "Walking",
    "restSeconds": null,
    "activeSeconds": 1722,
    "averageHeartRate": 99,
    "minHeartRate": 88,
    "maxHeartRate": 108,
    "appleHealthKitWorkoutType": 52,
    "googleHealthConnectWorkoutType": null,
    "calculatedRange": "Poor",
    "score": 1.8333333333333333,
    "sourceName": "Willie's Apple Watch"
  },
  {
    "startTime": "2025-11-26T07:25:55.564+00:00",
    "endTime": "2025-11-26T08:15:37.776+00:00",
    "durationSeconds": 2982,
    "totalEnergyBurnedKCal": 205,
    "totalDistanceMeters": 3157,
    "workoutType": "Walking",
    "restSeconds": null,
    "activeSeconds": 2982,
    "averageHeartRate": 105,
    "minHeartRate": 92,
    "maxHeartRate": 114,
    "appleHealthKitWorkoutType": 52,
    "googleHealthConnectWorkoutType": null,
    "calculatedRange": "Fair",
    "score": 2.8333333333333335,
    "sourceName": "Willie's Apple Watch"
  },
  {
    "startTime": "2025-11-25T07:16:41.237+00:00",
    "endTime": "2025-11-25T08:10:49.098+00:00",
    "durationSeconds": 3248,
    "totalEnergyBurnedKCal": 209,
    "totalDistanceMeters": 3196,
    "workoutType": "Walking",
    "restSeconds": null,
    "activeSeconds": 3248,
    "averageHeartRate": 111,
    "minHeartRate": 95,
    "maxHeartRate": 130,
    "appleHealthKitWorkoutType": 52,
    "googleHealthConnectWorkoutType": null,
    "calculatedRange": "Average",
    "score": 3,
    "sourceName": "Willie's Apple Watch"
  },
  {
    "startTime": "2025-11-24T07:23:12.337+00:00",
    "endTime": "2025-11-24T08:14:31.046+00:00",
    "durationSeconds": 3079,
    "totalEnergyBurnedKCal": 214,
    "totalDistanceMeters": 3192,
    "workoutType": "Walking",
    "restSeconds": null,
    "activeSeconds": 3079,
    "averageHeartRate": 108,
    "minHeartRate": 85,
    "maxHeartRate": 119,
    "appleHealthKitWorkoutType": 52,
    "googleHealthConnectWorkoutType": null,
    "calculatedRange": "Fair",
    "score": 2.8333333333333335,
    "sourceName": "Willie's Apple Watch"
  },
  {
    "startTime": "2025-11-23T11:06:25.107+00:00",
    "endTime": "2025-11-23T12:04:13.659+00:00",
    "durationSeconds": 3469,
    "totalEnergyBurnedKCal": 329,
    "totalDistanceMeters": null,
    "workoutType": "Traditional Strength Training",
    "restSeconds": null,
    "activeSeconds": 3469,
    "averageHeartRate": 112,
    "minHeartRate": 90,
    "maxHeartRate": 141,
    "appleHealthKitWorkoutType": 50,
    "googleHealthConnectWorkoutType": null,
    "calculatedRange": "Average",
    "score": 3.3333333333333335,
    "sourceName": "Zing"
  }
];

